/**
 * LE BANDEAU DE FIABILITÉ — CE QUE L'ÉCRAN EN DIT (lot 108, refondu au 110).
 *
 * Le comptage est fait par l'API (`/analyse/fiabilite`), qui applique aux
 * pièces le MÊME `partsDe` que le BFR. Ce fichier ne recompte rien : il met en
 * phrase, et range les lignes. Tout ce qui s'y trouve est pur, donc testable
 * sans monter d'écran.
 *
 * POURQUOI DEUX MONTANTS ET JAMAIS LEUR SOMME. Une créance échue et une dette
 * échue jouent en sens INVERSE sur le BFR : retirer la créance le fait baisser,
 * retirer la dette le fait monter. Un total unique donnerait un nombre qui ne
 * dit pas dans quel sens le chiffre est faussé — or c'est précisément ce que
 * l'exploitation cherche à voir (décision du 18/09/2026).
 */

/** Une pièce que le calcul répute réglée aujourd'hui, telle que l'API la rend. */
export interface PieceFiabilite {
  readonly cle: string;
  readonly numero: string | null;
  readonly tiers_nom: string;
  readonly echeance: string | null;
  readonly montant: number;
  readonly jours: number | null;
}

export interface CoteFiabiliteLu {
  readonly nombre: number;
  readonly montant: number;
  readonly pieces: readonly PieceFiabilite[];
}

/** Une pièce écartée des calculs faute de hors-taxe lisible (lot 113). */
export interface PieceSansHtLue {
  readonly cle: string;
  readonly numero: string | null;
  readonly tiers_nom: string;
  readonly date: string | null;
  readonly ttc: number;
}

export interface CoteSansHtLu {
  readonly nombre: number;
  readonly montant: number;
  readonly pieces: readonly PieceSansHtLue[];
}

export interface FiabiliteLue {
  readonly aujourdhui: string;
  readonly ecartees: number;
  readonly clients: CoteFiabiliteLu;
  readonly fournisseurs: CoteFiabiliteLu;
  readonly sans_ht_clients: CoteSansHtLu;
  readonly sans_ht_fournisseurs: CoteSansHtLu;
}

export type SensFiabilite = 'client' | 'fournisseur';

/** Une ligne de la liste dépliable : une pièce, et de quel côté elle est. */
export interface LigneFiabilite extends PieceFiabilite {
  readonly sens: SensFiabilite;
}

export interface ResumeFiabilite {
  /** Faux : il n'y a rien à signaler, le bandeau ne s'affiche pas. */
  readonly aDire: boolean;
  /** La phrase des pièces échues, ou nulle s'il n'y en a aucune. */
  readonly echues: string | null;
  /** La phrase des factures écartées à la main, ou nulle. */
  readonly ecartees: string | null;
  /** La phrase des pièces sans hors-taxe lisible (lot 113), ou nulle. */
  readonly sansHt: string | null;
  readonly nombrePieces: number;
}

function euros(centimes: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(centimes / 100);
}

/**
 * LA PHRASE DU BANDEAU. Le verbe est au féminin pluriel dans tous les cas :
 * « créances » et « dettes » le sont l'un comme l'autre.
 *
 * ELLE A CHANGÉ D'OBJET AU LOT 110. Elle disait « réputées réglées
 * aujourd'hui », parce que le calcul ramenait les pièces échues au jour même.
 * Cette invention de date a été supprimée : la phrase annonce désormais ce
 * qu'aucune date ne prévoit, ce qui est le vrai manque.
 */
export function resumeDe(f: FiabiliteLue | null): ResumeFiabilite {
  if (f === null) {
    return {
      aDire: false,
      echues: null,
      ecartees: null,
      sansHt: null,
      nombrePieces: 0,
    };
  }
  const c = f.clients.montant;
  const four = f.fournisseurs.montant;
  const cote = (montant: number, nom: string) => `${euros(montant)} ${nom}`;
  const fin = 'n’ont aucune date de règlement prévue.';
  const echues =
    c > 0 && four > 0
      ? `${cote(c, 'de créances clients')} et ${cote(four, 'de dettes fournisseurs')} ${fin}`
      : c > 0
        ? `${cote(c, 'de créances clients')} ${fin}`
        : four > 0
          ? `${cote(four, 'de dettes fournisseurs')} ${fin}`
          : null;
  const ecartees =
    f.ecartees <= 0
      ? null
      : f.ecartees === 1
        ? '1 facture est écartée des calculs à la main.'
        : `${String(f.ecartees)} factures sont écartées des calculs à la main.`;
  /**
   * LES PIÈCES SANS HORS-TAXE LISIBLE (lot 113).
   *
   * LE MONTANT ANNONCÉ EST UN TTC, et la phrase le dit. Parler de « chiffre
   * d'affaires manquant » reviendrait à annoncer un hors-taxe qu'on ne connaît
   * justement pas — c'est l'erreur que le lot 113 vient de corriger, il serait
   * malvenu de la refaire dans le texte qui l'annonce.
   *
   * LES DEUX CÔTÉS SONT ADDITIONNÉS ICI, contrairement au premier volet. Ils
   * ne jouent pas en sens inverse : une créance sans HT gonflait le CA, une
   * dette sans HT gonflait les charges, les deux faussaient la marge. Un seul
   * total a donc un sens, là où il n'en avait pas pour le BFR.
   */
  const nSansHt = f.sans_ht_clients.nombre + f.sans_ht_fournisseurs.nombre;
  const mSansHt = f.sans_ht_clients.montant + f.sans_ht_fournisseurs.montant;
  const sansHt =
    nSansHt <= 0
      ? null
      : `${String(nSansHt)} pièce${nSansHt > 1 ? 's' : ''} (${euros(mSansHt)} TTC) ` +
        `${nSansHt > 1 ? 'sont écartées' : 'est écartée'} du chiffre d’affaires, ` +
        'des charges et de la TVA : leur hors-taxe n’est pas lisible.';
  return {
    aDire: echues !== null || ecartees !== null || sansHt !== null,
    echues,
    ecartees,
    sansHt,
    nombrePieces: f.clients.nombre + f.fournisseurs.nombre,
  };
}

/**
 * LES LIGNES DE LA LISTE, les deux côtés mêlés et triés par montant
 * décroissant : la première ligne est celle qui pèse le plus sur l'écart, quel
 * que soit son côté. À montant égal, clients d'abord, puis par clé, pour que
 * l'ordre ne bouge pas d'un affichage à l'autre.
 */
export function lignesDe(f: FiabiliteLue | null): readonly LigneFiabilite[] {
  if (f === null) return [];
  const lignes: LigneFiabilite[] = [
    ...f.clients.pieces.map((p) => ({ ...p, sens: 'client' as const })),
    ...f.fournisseurs.pieces.map((p) => ({ ...p, sens: 'fournisseur' as const })),
  ];
  lignes.sort((a, b) => {
    if (b.montant !== a.montant) return b.montant - a.montant;
    if (a.sens !== b.sens) return a.sens === 'client' ? -1 : 1;
    return a.cle < b.cle ? -1 : 1;
  });
  return lignes;
}

/** « échue depuis 62 jours », ou « échéance inconnue » quand la pièce n'en a pas. */
export function retardLisible(l: LigneFiabilite): string {
  if (l.jours === null) return 'échéance inconnue';
  if (l.jours <= 0) return 'échue aujourd’hui';
  return `échue depuis ${String(l.jours)} jour${l.jours > 1 ? 's' : ''}`;
}

export const LIBELLES_SENS: Readonly<Record<SensFiabilite, string>> = {
  client: 'Client',
  fournisseur: 'Fournisseur',
};
